import { Typography } from "@mui/joy";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";

type Content = {
  title?: string;
  options?: Options | undefined | null;
  actions?: string[];
};

type Options = {
  subtitle?: string | ReactNode;
  render?: (c: any) => ReactElement;
  icon?: ReactNode;
};

export const AskQuestionContext = createContext(
  (title: string, actions: string[], options?: Options | undefined | null) =>
    new Promise<number>((r) => r(1))
);

/**
 * @param root0
 * @param root0.children
 */
export default function AskQuestion({ children }: { children: any }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<Content>({});
  // Bridges the promise returned by ask() to the dialog buttons. Kept in a ref
  // (not module scope) so that overlapping dialogs cannot resolve each other's
  // promise. Note: there is intentionally no global "Enter" key handler here —
  // these dialogs confirm destructive actions, so a reflexive Enter must not
  // auto-trigger the primary button.
  const resolveRef = useRef<
    ((value: number | PromiseLike<number>) => void) | null
  >(null);

  const ask = (
    title: string,
    actions: string[],
    options?: Options | undefined | null
  ) => {
    setContent({
      title,
      actions,
      options,
    });
    setOpen(true);

    return new Promise<number>((r) => {
      resolveRef.current = r;
    });
  };

  return (
    <AskQuestionContext.Provider value={ask}>
      {children}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          resolveRef.current?.(0);
        }}
      >
        {content.title ? (
          <DialogTitle>
            {content.options?.icon}
            {content.title}
          </DialogTitle>
        ) : (
          []
        )}
        <DialogContent>
          <Typography>{content.options?.subtitle}</Typography>
          {content.options?.render ? (
            <Typography>
              {content.options.render(() => {
                setOpen(false);
                resolveRef.current?.(0);
              })}
            </Typography>
          ) : (
            []
          )}
        </DialogContent>
        {content.actions?.length ? (
          <DialogActions>
            {content.actions.map((a, i) => (
              <Button
                variant={
                  Number(i) === (content.actions?.length ?? 0) - 1
                    ? "contained"
                    : "text"
                }
                onClick={() => {
                  setOpen(false);
                  resolveRef.current?.(i);
                }}
                key={a}
              >
                {a}
              </Button>
            ))}
          </DialogActions>
        ) : (
          []
        )}
      </Dialog>
    </AskQuestionContext.Provider>
  );
}

export const useAskQuestion = () => {
  const context = useContext(AskQuestionContext);
  return context;
};
